import { createAsyncThunk } from "@reduxjs/toolkit";

export const fetchPatientProfile = createAsyncThunk("clinic/fetchPatientProfile", async(_, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patients/`;
    const options = {
        method: "GET",
        headers: {
            'Content-Type' : 'application/json',
            Authorization: `Bearer ${token}`,
        },
    };
    try {
        const response = await fetch(uri, options);
        if(!response.ok){
            throw new Error('Error al cargar el perfil clínico.');
        }
        const data = await response.json();
        if (Array.isArray(data)) return data[0] || null;
        if (data && Array.isArray(data.results)) return data.results[0] || null;
        return data || null;
    } catch (error){
        return thunkAPI.rejectWithValue(error.message);
    }
});

export const fetchPatientsList = createAsyncThunk("clinic/fetchPatientsList", async ({ page = 1, q = "", procedure = ""} = {}, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;

    const params = new URLSearchParams();
    if(page) params.set("page", page);
    if(q) params.set("q", q)
    if(procedure) params.set("procedure", procedure)

    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patients/?${params.toString()}`;
    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        }
    };
    try {
        const response = await fetch(uri, options);
        if(!response.ok){
            throw new Error("No se pudo obtener la lista de pacientes.")
        }
        const data = await response.json();
        return { ...data, page, q, procedure };
    } catch (error) {
        return thunkAPI.rejectWithValue(error.message);
    }
});

export const fetchPatientById = createAsyncThunk("clinic/fetchPatientById", async (patientId, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patients/${patientId}/`;
    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        }
    };
    try {
        const response = await fetch(uri, options);
        if(!response.ok){
            throw new Error("No se pudo obtener el paciente.")
        }
        return await response.json();
    } catch (error){
        return thunkAPI.rejectWithValue(error.message);
    }
});

export const updatePatientById = createAsyncThunk("clinic/updatePatientById", async({id, payload}, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patients/${id}/`;
    const options = {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    };
    try {
        const response = await fetch(uri, options);
        const data = await response.json().catch(() => ({}));
        if (!response.ok){
            throw new Error(data?.detail || "No se pudo guardar.");
        }
        return data;
    } catch (error){
        return thunkAPI.rejectWithValue(error.message);
    }
});

export const deletePatientById = createAsyncThunk(
  "clinic/deletePatientById",
  async (patientId, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;

    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patients/${patientId}/`;
    const options = {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.detail || "No se pudo eliminar el paciente.");
      }
      return { id: patientId };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchMedicalHistory = createAsyncThunk("clinic/fetchMedicaHistory", async (patientId, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/medical-histories/?patient=${patientId}`;
    const options = {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    };
    try {
        const response = await fetch(uri, options);
        if(!response.ok){
            throw new Error ("No se pudo cargar el historial médico.");
        }
        return await response.json();
    } catch (error){
        return thunkAPI.rejectWithValue(error.message);
    }
});

export const createMedicalHistory = createAsyncThunk("clinic/createMedicalHistory", async({patientId, data}, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/medical-histories/`;
    const options = {
        method: 'POST',
        headers: {
            "Content-Type" : "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...data, patient: patientId}),
    };
    try {
        const response = await fetch(uri, options);
        const result = await response.json().catch(() => ({}));
        if(!response.ok){
            throw new Error(result?.detail || "No se pudo crear la condición.");
        }
        return result;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.message);
    }
});

export const updateMedicalHistory = createAsyncThunk("clinic/updateMedicalHistory", async({id, data}, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/medical-histories/${id}/`;
    const options = {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
    };
    try{
        const response = await fetch(uri, options);
        const result = await response.json().catch(() => ({}));
        if(!response.ok){
            throw new Error (result?.detail || "No se pudo actualizar la condición.");
        }
        return result;
    } catch (error){
        return thunkAPI.rejectWithValue(error.message);
    }
});

export const deleteMedicalHistory = createAsyncThunk(
  "clinic/deleteMedicalHistory",
  async (id, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/medical-histories/${id}/`;
    const options = { method: "DELETE", headers: { Authorization: `Bearer ${token}` } };
    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudo eliminar la condición.");
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/* =========================
   Allergies
   ========================= */

export const fetchAllergies = createAsyncThunk(
  "clinic/fetchAllergies",
  async (patientId, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/allergies/?patient=${patientId}`;
    const options = {
      method: "GET",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    };
    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudieron cargar las alergias.");
      return await response.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const createAllergy = createAsyncThunk(
  "clinic/createAllergy",
  async ({ patientId, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/allergies/`;
    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...data, patient: patientId }),
    };
    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.detail || "No se pudo crear la alergia.");
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateAllergy = createAsyncThunk(
  "clinic/updateAllergy",
  async ({ id, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/allergies/${id}/`;
    const options = {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.detail || "No se pudo actualizar la alergia.");
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteAllergy = createAsyncThunk(
  "clinic/deleteAllergy",
  async (id, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/allergies/${id}/`;
    const options = { method: "DELETE", headers: { Authorization: `Bearer ${token}` } };
    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudo eliminar la alergia.");
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/* =========================
   Medications
   ========================= */

export const fetchMedications = createAsyncThunk(
  "clinic/fetchMedications",
  async (patientId, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/medications/?patient=${patientId}`;
    const options = {
      method: "GET",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    };
    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudieron cargar las medicaciones.");
      return await response.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const createMedication = createAsyncThunk(
  "clinic/createMedication",
  async ({ patientId, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/medications/`;
    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...data, patient: patientId }),
    };
    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.detail || "No se pudo crear la medicación.");
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateMedication = createAsyncThunk(
  "clinic/updateMedication",
  async ({ id, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/medications/${id}/`;
    const options = {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.detail || "No se pudo actualizar la medicación.");
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteMedication = createAsyncThunk(
  "clinic/deleteMedication",
  async (id, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/medications/${id}/`;
    const options = { method: "DELETE", headers: { Authorization: `Bearer ${token}` } };
    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudo eliminar la medicación.");
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/* =========================
   Surgeries
   ========================= */

export const fetchSurgeries = createAsyncThunk(
  "clinic/fetchSurgeries",
  async (patientId, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/surgeries/?patient=${patientId}`;
    const options = {
      method: "GET",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    };
    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudieron cargar las cirugías.");
      return await response.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const createSurgery = createAsyncThunk(
  "clinic/createSurgery",
  async ({ patientId, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/surgeries/`;
    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...data, patient: patientId }),
    };
    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.detail || "No se pudo crear la cirugía.");
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateSurgery = createAsyncThunk(
  "clinic/updateSurgery",
  async ({ id, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/surgeries/${id}/`;
    const options = {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.detail || "No se pudo actualizar la cirugía.");
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteSurgery = createAsyncThunk(
  "clinic/deleteSurgery",
  async (id, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/surgeries/${id}/`;
    const options = { method: "DELETE", headers: { Authorization: `Bearer ${token}` } };
    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudo eliminar la cirugía.");
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/* =========================
   Recovery Notes
   ========================= */

export const fetchRecoveryNotes = createAsyncThunk(
  "clinic/fetchRecoveryNotes",
  async ({ patientId, surgeryId } = {}, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const params = new URLSearchParams();
    if (patientId) params.set("patient", patientId);
    if (surgeryId) params.set("surgery", surgeryId); // por si luego lo soportas
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/recovery-notes/?${params.toString()}`;
    const options = {
      method: "GET",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    };
    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudieron cargar los seguimientos.");
      return await response.json();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const createRecoveryNote = createAsyncThunk(
  "clinic/createRecoveryNote",
  async ({ surgeryId, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/recovery-notes/`;
    // data puede incluir medications_taken_ids: [ids]
    const payload = { ...data, surgery: surgeryId };
    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    };
    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.detail || "No se pudo crear el seguimiento.");
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateRecoveryNote = createAsyncThunk(
  "clinic/updateRecoveryNote",
  async ({ id, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/recovery-notes/${id}/`;
    const options = {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.detail || "No se pudo actualizar el seguimiento.");
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteRecoveryNote = createAsyncThunk(
  "clinic/deleteRecoveryNote",
  async (id, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/recovery-notes/${id}/`;
    const options = { method: "DELETE", headers: { Authorization: `Bearer ${token}` } };
    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudo eliminar el seguimiento.");
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

/* =========================
   Clinical Documents (sensibles)
   ========================= */

export const fetchClinicalDocuments = createAsyncThunk(
  "clinic/fetchClinicalDocuments",
  async ({ patientId, page = 1 } = {}, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;

    const params = new URLSearchParams();
    if (patientId) params.set("patient", patientId);
    if (page) params.set("page", page);

    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/clinical-documents/?${params.toString()}`;
    const options = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudieron cargar los documentos clínicos.");

      const data = await response.json();

      if (Array.isArray(data)) {
        return { results: data, count: data.length, next: null, previous: null, page, patientId };
      }

      const results = Array.isArray(data?.results) ? data.results : [];
      return {
        results,
        count: data?.count ?? results.length,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        page,
        patientId,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const uploadClinicalDocument = createAsyncThunk(
  "clinic/uploadClinicalDocument",
  async ({ patientId, file, category = "other", description = "" }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;

    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/clinical-documents/`;
    const formData = new FormData();
    formData.append("patient", String(patientId));
    formData.append("category", category);
    formData.append("description", description);
    formData.append("file", file);

    const options = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    };

    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.detail || "No se pudo subir el documento.");
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteClinicalDocument = createAsyncThunk(
  "clinic/deleteClinicalDocument",
  async (documentId, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;

    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/clinical-documents/${documentId}/`;
    const options = {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudo eliminar el documento.");
      return { id: documentId };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const downloadClinicalDocument = createAsyncThunk(
  "clinic/downloadClinicalDocument",
  async (documentId, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;

    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/clinical-documents/${documentId}/download/`;
    const options = {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudo descargar el documento.");

      const contentDisposition =
        response.headers.get("Content-Disposition") || "";
      let filename = "documento";
      const match = contentDisposition.match(/filename="?([^"]+)"?/i);
      if (match && match[1]) filename = match[1];

      const blob = await response.blob();

      // 👉 En vez de devolver el Blob, creamos una object URL (string)
      const objectUrl = URL.createObjectURL(blob);

      return { documentId, filename, objectUrl };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "No se pudo descargar el documento."
      );
    }
  }
);

export const createPatient = createAsyncThunk(
  "clinic/createPatient",
  async ({ username, first_name, last_name, email = "", phone = "" }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;

    const uri = `${process.env.REACT_APP_BACKEND_URL}/users/staff/create-patient/`;

    const bodyPayload = {
      username: (username || "").trim(),
      first_name: (first_name || "").trim(),
      last_name: (last_name || "").trim(),
      email: (email || "").trim(),
      phone: (phone || "").trim(),
    };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(bodyPayload),
    };

    try {
      const response = await fetch(uri, options);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        let errorMessage = "No se pudo crear el paciente.";

        if (data && typeof data === "object") {
          if (data.detail) {
            // Caso {"detail": "..."}
            errorMessage = data.detail;
          } else {
            // Casos tipo {"email": ["..."]} o {"username": ["..."]}
            const firstKey = Object.keys(data)[0];
            const firstValue = data[firstKey];

            if (Array.isArray(firstValue) && firstValue[0]) {
              errorMessage = firstValue[0];
            } else if (typeof firstValue === "string") {
              errorMessage = firstValue;
            }
          }
        }

        throw new Error(errorMessage);
      }

      // { user: {...}, patient_id: number }
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message || "Error al crear paciente.");
    }
  }
);

/* =========================
   Patient Cases (casos clínicos globales)
   ========================= */

export const fetchPatientCases = createAsyncThunk(
  "clinic/fetchPatientCases",
  async ({ patientId, page = 1 } = {}, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;

    const params = new URLSearchParams();
    if (patientId) params.set("patient", patientId);
    if (page) params.set("page", page);

    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patient-cases/?${params.toString()}`;
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        throw new Error("No se pudieron cargar los casos clínicos.");
      }

      const data = await response.json();

      // Soportar tanto lista simple como respuesta paginada
      if (Array.isArray(data)) {
        return {
          results: data,
          count: data.length,
          next: null,
          previous: null,
          page,
          patientId: patientId || null,
        };
      }

      const results = Array.isArray(data?.results) ? data.results : [];
      return {
        results,
        count: data?.count ?? results.length,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        page,
        patientId: patientId || null,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const createPatientCase = createAsyncThunk(
  "clinic/createPatientCase",
  async ({ patientId, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patient-cases/`;

    const payload = {
      ...data,
      patient: patientId,
    };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    };

    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          result?.detail || "No se pudo crear el caso clínico."
        );
      }
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Error al crear el caso clínico."
      );
    }
  }
);

export const updatePatientCase = createAsyncThunk(
  "clinic/updatePatientCase",
  async ({ id, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patient-cases/${id}/`;

    const options = {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    };

    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          result?.detail || "No se pudo actualizar el caso clínico."
        );
      }
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Error al actualizar el caso clínico."
      );
    }
  }
);

export const deletePatientCase = createAsyncThunk(
  "clinic/deletePatientCase",
  async (id, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;
    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patient-cases/${id}/`;

    const options = {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data?.detail || "No se pudo eliminar el caso clínico."
        );
      }
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.message || "Error al eliminar el caso clínico."
      );
    }
  }
);

/* =========================
   Patient Journey (línea del tiempo por fases)
   ========================= */

export const fetchPatientJourneyItems = createAsyncThunk(
  "clinic/fetchPatientJourneyItems",
  async (
    { patientId, courseId = null, phase = "", area = "", status = "", page = 1 } = {},
    thunkAPI
  ) => {
    const { token } = thunkAPI.getState().auth;

    const params = new URLSearchParams();
    if (patientId) params.set("patient", patientId);
    if (courseId) params.set("course", courseId);
    if (phase) params.set("phase", phase);
    if (area) params.set("area", area);
    if (status) params.set("status", status);
    if (page) params.set("page", page);

    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patient-journey/?${params.toString()}`;
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) throw new Error("No se pudieron cargar los ítems del seguimiento.");

      const data = await response.json();

      // Soportar lista simple y paginación DRF
      if (Array.isArray(data)) {
        return {
          results: data,
          count: data.length,
          next: null,
          previous: null,
          page,
          patientId: patientId || null,
          courseId: courseId || null,
          phase,
          area,
          status,
        };
      }

      const results = Array.isArray(data?.results) ? data.results : [];
      return {
        results,
        count: data?.count ?? results.length,
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        page,
        patientId: patientId || null,
        courseId: courseId || null,
        phase,
        area,
        status,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const createPatientJourneyItem = createAsyncThunk(
  "clinic/createPatientJourneyItem",
  async ({ patientId, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;

    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patient-journey/`;
    const payload = { ...data, patient: patientId };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    };

    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.detail || "No se pudo crear el ítem del seguimiento.");
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updatePatientJourneyItem = createAsyncThunk(
  "clinic/updatePatientJourneyItem",
  async ({ id, data }, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;

    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patient-journey/${id}/`;
    const options = {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    };

    try {
      const response = await fetch(uri, options);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result?.detail || "No se pudo actualizar el ítem del seguimiento.");
      return result;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deletePatientJourneyItem = createAsyncThunk(
  "clinic/deletePatientJourneyItem",
  async (id, thunkAPI) => {
    const { token } = thunkAPI.getState().auth;

    const uri = `${process.env.REACT_APP_BACKEND_URL}/clinic/patient-journey/${id}/`;
    const options = {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    };

    try {
      const response = await fetch(uri, options);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.detail || "No se pudo eliminar el ítem del seguimiento.");
      }
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);